import React from 'react';
import NeuroInformedBadge, { NeuroTechniqueList } from '../NeuroInformedBadge';
import HelperIcon from '../HelperIcon';
import { helperContent } from '../../content/helper-content';

/**
 * SummaryView - Displays meeting summary with student linking
 *
 * Supports summaries from:
 * - GOOGLE: Built-in Gemini meeting notes from "Take notes for me" feature
 * - AI_GENERATED: Custom AI-generated summaries (legacy)
 */
const SummaryView = ({
  summary,
  summarySource,
  summaryGeneratedAt,
  googleNotesDocId,
  loading,
  error,
  onRegenerate,
  onRetrySection,
  retryingSectionKey,
  onAiSummarize,
  isAnalyzingTranscript
}) => {
  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };
  // Loading state
  if (loading) {
    return (
      <div className="summary-view loading-state">
        <div className="summary-loading">
          <div className="loading-spinner"></div>
          <p>Fetching meeting notes...</p>
          <p className="loading-hint">Looking for Google meeting notes</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="summary-view error-state">
        <div className="summary-error">
          <div className="error-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <h4>Unable to Load Summary</h4>
          <p className="error-detail">{error}</p>
          <div className="error-suggestions">
            <p>Suggestions:</p>
            <ul>
              <li>Enable "Take notes for me" in Google Meet settings</li>
              <li>Wait 15-45 minutes after the meeting ends</li>
              <li>Check that you have access to the meeting notes document</li>
            </ul>
          </div>
          {onRegenerate && (
            <button className="btn btn-secondary" onClick={onRegenerate}>
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // Parse summary if it's a string
  let parsedSummary = summary || null;
  if (typeof summary === 'string') {
    try {
      parsedSummary = JSON.parse(summary);
    } catch {
      // Keep as string if not valid JSON
      parsedSummary = { session_overview: summary };
    }
  }
  const rawOverviewText = typeof summary === 'string'
    ? summary
    : parsedSummary?.session_overview || '';
  const normalizedOverview = typeof rawOverviewText === 'string'
    ? rawOverviewText.trim().toLowerCase()
    : '';
  const isGoogleNotesFallbackText = normalizedOverview.includes('no google meeting notes available');
  const shouldShowAiSummaryCta = !summary || summarySource === 'NONE' || isGoogleNotesFallbackText;

  const sectionConfig = {
    content_analysis: {
      title: 'Content Analysis',
      fields: [
        ['topics_covered', 'Topics Covered'],
        ['learning_outcomes', 'Learning Outcomes'],
        ['session_overview', 'Session Overview']
      ]
    },
    engagement_analysis: {
      title: 'Engagement Analysis',
      fields: [
        ['student_engagement', 'Student Engagement'],
        ['what_worked_well', 'What Worked Well'],
        ['emotional_climate', 'Learning Environment']
      ]
    },
    recommendations: {
      title: 'Challenges & Recommendations',
      fields: [
        ['challenges_identified', 'Challenges Identified'],
        ['next_steps', 'Next Steps'],
        ['neuro_techniques_observed', 'Neuro Techniques Observed']
      ]
    }
  };

  const sectionedSummary = parsedSummary?.sections && typeof parsedSummary.sections === 'object'
    ? parsedSummary.sections
    : null;
  const hasSectionedSummary = Boolean(sectionedSummary);

  const renderList = (items, emptyMessage = 'None identified') => {
    if (!items || items.length === 0) {
      return <p className="empty-list">{emptyMessage}</p>;
    }
    return (
      <ul>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  };

  const renderFieldValue = (key, label, value) => {
    const fieldThemeClass = key === 'challenges_identified'
      ? 'summary-subblock-challenges'
      : key === 'what_worked_well' || key === 'student_engagement'
        ? 'summary-subblock-success'
        : key === 'next_steps'
          ? 'summary-subblock-next-steps'
          : '';
    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      return (
        <div key={key} className={`summary-section-subblock ${fieldThemeClass}`.trim()}>
          <h5>{label}</h5>
          {renderList(value)}
        </div>
      );
    }
    if (typeof value === 'string' && value.trim()) {
      return (
        <div key={key} className={`summary-section-subblock ${fieldThemeClass}`.trim()}>
          <h5>{label}</h5>
          <p>{value}</p>
        </div>
      );
    }
    return null;
  };

  const renderSectionBadge = (status) => {
    if (status === 'complete') return <span className="badge badge-success">Complete</span>;
    if (status === 'running') return <span className="badge badge-info">Running</span>;
    if (status === 'failed') return <span className="badge badge-warning">Needs Retry</span>;
    return <span className="badge badge-neutral">Pending</span>;
  };

  if (shouldShowAiSummaryCta) {
    return (
      <div className="summary-view empty-state">
        <div className="no-summary-content">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
          </div>
          <h4>Transcript Summary</h4>
          <p>Run AI analysis to generate content, engagement, and next-step insights.</p>
          {onAiSummarize && (
            <button
              className="btn btn-primary"
              onClick={onAiSummarize}
              disabled={isAnalyzingTranscript}
            >
              {isAnalyzingTranscript ? 'Summarizing...' : 'AI Summarize Transcript'}
            </button>
          )}
          {onRegenerate && (
            <button className="btn btn-secondary btn-small" onClick={onRegenerate}>
              Refresh Google Notes
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="summary-view">
      {/* Overview */}
      {parsedSummary?.session_overview && !isGoogleNotesFallbackText && (
        <div className="summary-section overview-section">
          <div className="section-header">
            <h4>Session Overview</h4>
            <HelperIcon
              title={helperContent.lrbApproach.title}
              content={helperContent.lrbApproach.content}
              size="small"
            />
          </div>
          <p className="overview-text">{parsedSummary.session_overview}</p>
          {/* Display neuro-informed badge if techniques were observed */}
          {parsedSummary.neuro_techniques_observed && parsedSummary.neuro_techniques_observed.length > 0 && (
            <NeuroTechniqueList techniques={parsedSummary.neuro_techniques_observed} />
          )}
        </div>
      )}

      {hasSectionedSummary ? (
        <div className="summary-sections-grid">
          {Object.entries(sectionConfig).map(([sectionKey, config]) => {
            const section = sectionedSummary[sectionKey] || {};
            const sectionData = section.data || {};
            const status = section.status || 'pending';
            const fieldNodes = config.fields
              .map(([fieldKey, label]) => renderFieldValue(fieldKey, label, sectionData[fieldKey]))
              .filter(Boolean);

            return (
              <div
                key={sectionKey}
                className={`summary-section section-card section-${status} section-${sectionKey}`}
              >
                <div className="section-header">
                  <h4>{config.title}</h4>
                  {renderSectionBadge(status)}
                </div>
                {status === 'running' && (
                  <div className="section-running-indicator">
                    <span className="btn-spinner-small" aria-hidden="true"></span>
                    <span>Analyzing...</span>
                  </div>
                )}

                {section.error && (
                  <p className="section-error">{section.error}</p>
                )}

                {fieldNodes.length > 0 ? fieldNodes : (
                  <p className="empty-list">
                    {status === 'failed'
                      ? 'This section failed. Retry to regenerate it.'
                      : 'No section output yet.'}
                  </p>
                )}

                {status === 'failed' && onRetrySection && (
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => onRetrySection(sectionKey)}
                    disabled={retryingSectionKey === sectionKey}
                  >
                    {retryingSectionKey === sectionKey ? 'Retrying...' : 'Retry Section'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {/* Topics Covered */}
          {parsedSummary.topics_covered && parsedSummary.topics_covered.length > 0 && (
            <div className="summary-section">
              <h4>Topics Covered</h4>
              {renderList(parsedSummary.topics_covered)}
            </div>
          )}

          {/* Learning Outcomes */}
          {parsedSummary.learning_outcomes && parsedSummary.learning_outcomes.length > 0 && (
            <div className="summary-section">
              <h4>Learning Outcomes</h4>
              {renderList(parsedSummary.learning_outcomes)}
            </div>
          )}

          {/* Challenges */}
          {parsedSummary.challenges_identified && parsedSummary.challenges_identified.length > 0 && (
            <div className="summary-section challenges">
              <h4>Challenges Identified</h4>
              {renderList(parsedSummary.challenges_identified)}
            </div>
          )}

          {/* What Worked Well */}
          {parsedSummary.what_worked_well && parsedSummary.what_worked_well.length > 0 && (
            <div className="summary-section success">
              <h4>What Worked Well</h4>
              {renderList(parsedSummary.what_worked_well)}
            </div>
          )}

          {/* Next Steps */}
          {parsedSummary.next_steps && parsedSummary.next_steps.length > 0 && (
            <div className="summary-section next-steps">
              <h4>Next Steps</h4>
              {renderList(parsedSummary.next_steps)}
            </div>
          )}

          {/* Emotional Climate (from LRB neuro-informed analysis) */}
          {parsedSummary.emotional_climate && (
            <div className="summary-section emotional-climate">
              <div className="section-header">
                <h4>Learning Environment</h4>
                <HelperIcon
                  title={helperContent.emotionalClimate.title}
                  content={helperContent.emotionalClimate.content}
                  size="small"
                />
              </div>
              <p className="emotional-climate-text">{parsedSummary.emotional_climate}</p>
            </div>
          )}
        </>
      )}

      {/* Source Indicator and Actions */}
      <div className="summary-footer">
        <div className="summary-source-info">
          {summarySource === 'GOOGLE' && (
            <div className="google-notes-badge">
              <span className="badge badge-google">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                Google Meeting Notes
              </span>
              {googleNotesDocId && (
                <a
                  href={`https://docs.google.com/document/d/${googleNotesDocId}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-doc-link"
                  title="View original Google Doc"
                >
                  Open in Docs
                </a>
              )}
            </div>
          )}
          {summarySource === 'AI_GENERATED' && (
            <NeuroInformedBadge size="small" variant="subtle" />
          )}
          {summaryGeneratedAt && (
            <span className="summary-timestamp" title={new Date(summaryGeneratedAt).toLocaleString()}>
              Updated {formatRelativeTime(summaryGeneratedAt)}
            </span>
          )}
        </div>
        {onRegenerate && (
          <div className="summary-actions">
            <button
              className="btn btn-secondary btn-small"
              onClick={onRegenerate}
              title="Re-fetch meeting notes from Google"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
              </svg>
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryView;
